/**
 * PageHeader organism - consistent page header with title, breadcrumbs, and actions
 * Provides standard page header layout across the application
 */

import React from 'react';
import {
  HStack,
  VStack,
  Divider,
  Box,
  useColorModeValue,
  Badge,
} from '@chakra-ui/react';
import { PageTitle } from '../../atoms/typography/Text';
import {
  Breadcrumb,
  BreadcrumbProps,
} from '../../molecules/navigation/Breadcrumb';
import { BaseComponentProps } from '../../types';

export interface PageHeaderProps extends BaseComponentProps {
  /** Page title */
  title: string;
  /** Optional subtitle */
  subtitle?: string;
  /** Breadcrumb navigation */
  breadcrumb?: BreadcrumbProps;
  /** Action buttons */
  actions?: React.ReactNode;
  /** Whether to show divider below header */
  showDivider?: boolean;
  /** Optional icon for the page */
  icon?: React.ReactElement;
  /** Optional badge for status/count */
  badge?: {
    text: string;
    colorScheme?: string;
    variant?: 'solid' | 'outline' | 'subtle';
  };
  /** Enhanced styling variant */
  variant?: 'default' | 'elevated' | 'minimal';
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  breadcrumb,
  actions,
  showDivider = true,
  icon,
  badge,
  variant = 'default',
  className,
  testId,
}) => {
  const dividerColor = useColorModeValue('gray.200', 'gray.600');
  const headerBg = useColorModeValue('white', 'gray.800');
  const subtitleColor = useColorModeValue('gray.600', 'gray.400');
  const shadowColor = useColorModeValue(
    '0px 1px 3px rgba(0, 0, 0, 0.1)',
    '0px 1px 3px rgba(0, 0, 0, 0.3)',
  );
  const iconBg = useColorModeValue('blue.50', 'blue.900');
  const iconColor = useColorModeValue('blue.600', 'blue.300');

  const getVariantStyles = () => {
    switch (variant) {
      case 'elevated':
        return {
          bg: headerBg,
          borderRadius: 'lg',
          p: 6,
          boxShadow: shadowColor,
          border: '1px solid',
          borderColor: dividerColor,
        };
      case 'minimal':
        return {
          p: 4,
        };
      default:
        return {
          bg: headerBg,
          p: 6,
          borderRadius: 'md',
          border: '1px solid',
          borderColor: dividerColor,
        };
    }
  };

  return (
    <Box className={className} data-testid={testId} {...getVariantStyles()}>
      <VStack spacing={6} align="stretch">
        {/* Breadcrumb Navigation */}
        {breadcrumb && <Breadcrumb {...breadcrumb} />}

        {/* Title Section */}
        <VStack justify="space-between" align="flex-start">
          <HStack align="flex-start" spacing={4} flex="1">
            {/* Icon */}
            {icon && (
              <Box p={3} bg={iconBg} borderRadius="xl" color={iconColor}>
                {React.cloneElement(icon, { size: 24 })}
              </Box>
            )}

            {/* Title and Subtitle */}
            <VStack align="flex-start" spacing={3} flex="1">
              <PageTitle size="lg" fontWeight="bold">
                {title}
              </PageTitle>

              {subtitle && (
                <Box
                  color={subtitleColor}
                  fontSize="lg"
                  lineHeight="1.6"
                  maxW="600px"
                >
                  {subtitle}
                </Box>
              )}
              {badge && (
                <Badge
                  colorScheme={badge.colorScheme || 'blue'}
                  variant={badge.variant || 'subtle'}
                  fontSize="sm"
                  px={3}
                  py={1}
                  borderRadius="full"
                >
                  {badge.text}
                </Badge>
              )}
            </VStack>
          </HStack>

          {/* Actions */}
          {actions && (
            <HStack spacing={3} flexShrink={0} align="flex-start">
              {actions}
            </HStack>
          )}
        </VStack>

        {showDivider && variant !== 'minimal' && (
          <Divider borderColor={dividerColor} />
        )}
      </VStack>
    </Box>
  );
};
